import { Link } from "react-router-dom";
import { ArrowRight, Check, Star, Sparkles, Layers, ScanLine, FileText, MessageSquare, Scale } from "lucide-react";
import { useAuth, loginWithGoogle } from "@/lib/auth";

export default function Landing() {
  const { user, loading } = useAuth();
  const go = () => {
    // If we already have a user session (including the bypass-mode guest), go straight in.
    if (user) {
      window.location.href = "/dashboard";
      return;
    }
    // Otherwise, hand off to Google OAuth. If the backend is in bypass mode and the user
    // hasn't loaded yet, navigating to /dashboard will get them in via the guest fallback.
    if (loading) return;
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* NAV minimalista Apple */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-black/[0.05]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-7 h-7 rounded-full bg-foreground grid place-items-center">
              <span className="deco-star text-white w-3.5 h-3.5" />
            </div>
            <span className="font-display text-lg leading-none tracking-tight">
              <span className="font-black">strato</span>
              <span className="font-normal text-foreground/55 ml-1.5 text-sm">by abstudio</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-foreground/70">
            <a href="#how" className="hover:text-foreground transition" data-testid="nav-how">Cómo funciona</a>
            <a href="#what" className="hover:text-foreground transition" data-testid="nav-features">Qué hace</a>
            <a href="#pricing" className="hover:text-foreground transition" data-testid="nav-pricing">Precio</a>
          </nav>
          <button
            onClick={go}
            className="btn-pill bg-foreground hover:bg-black text-white text-sm px-5 py-2.5"
            data-testid="cta-launch"
          >
            {user ? "Entrar" : "Empezar"}
          </button>
        </div>
      </header>

      {/* ──────────────────────────────────────────────────────────────
          SECCIÓN 1 — HERO Apple/Notion minimalista con blobs
          ────────────────────────────────────────────────────────────── */}
      <section className="relative max-w-6xl mx-auto px-6 pt-20 pb-32 text-center">
        {/* Blobs orgánicos detrás */}
        <div className="blob blob-mint w-[420px] h-[420px] -top-10 left-1/4" />
        <div className="blob blob-rose w-[380px] h-[380px] top-20 right-1/4" style={{ animationDelay: "2s" }} />
        <div className="blob blob-lavender w-[300px] h-[300px] top-40 left-10" style={{ animationDelay: "4s" }} />

        <div className="relative">
          <span className="pill" data-testid="hero-pill">
            <Sparkles className="w-3.5 h-3.5 text-foreground" /> Tu aliado estratégico
          </span>
          <h1 className="mt-7 font-display font-black text-5xl sm:text-7xl lg:text-[88px] tracking-[-0.04em] leading-[0.95]">
            Cobra lo que vale<br />
            tu trabajo.{" "}
            <span className="scribble-underline scribble-mint">Sin estrés.</span>
          </h1>
          <p className="mt-8 text-base sm:text-lg text-foreground/65 max-w-xl mx-auto leading-relaxed">
            Strato es la herramienta que ayuda a freelancers creativos a precificar sus servicios, estructurar ofertas, analizar clientes y escribir propuestas premium.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <button onClick={go} className="btn-pill bg-foreground hover:bg-black text-white text-base px-7 py-3.5" data-testid="hero-cta-primary">
              Empezar gratis 5 días <ArrowRight className="w-4 h-4" />
            </button>
            <a href="#how" className="text-sm text-foreground/65 hover:text-foreground transition font-medium">Ver cómo funciona →</a>
          </div>
          <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-foreground/55">
            <Check className="w-3.5 h-3.5 text-emerald-600" /> $6/mes · cancela cuando quieras
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          SECCIÓN 2 — Quote big Apple-style (bloque ink)
          ────────────────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="block block-ink p-12 md:p-20 text-center relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-white/5 blur-2xl" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.03em] leading-[1.1] relative">
            Cobrar más<br />
            no es trabajar más.<br />
            Es asumir{" "}
            <span className="bg-[#EAC119] text-foreground px-3 rounded-2xl whitespace-nowrap">más responsabilidad.</span>
          </h2>
          <p className="mt-8 text-white/55 text-sm uppercase tracking-[0.2em] font-semibold relative">— La filosofía Strato</p>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          SECCIÓN 3 — "Cómo funciona" tipo Duolingo (3 pasos numerados)
          ────────────────────────────────────────────────────────────── */}
      <section id="how" className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <span className="pill mb-4"><span className="w-1.5 h-1.5 rounded-full bg-[#A8D5BA]" /> 3 pasos</span>
          <h2 className="mt-4 font-display text-4xl md:text-6xl font-black tracking-[-0.03em] leading-[1]">
            Tu primera oferta lista<br />en <span className="scribble-underline scribble-coral">menos de 2 minutos.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* Línea conectora subtil */}
          <svg className="hidden md:block absolute top-12 left-1/2 -translate-x-1/2 w-2/3 h-20 pointer-events-none" viewBox="0 0 600 80" fill="none">
            <path d="M 50 40 Q 200 -10 300 40 Q 400 90 550 40" stroke="#0A0A0A" strokeWidth="2" strokeDasharray="4 6" strokeLinecap="round" />
          </svg>

          {[
            { n: 1, color: "#A8D5BA", title: "Cuéntame el contexto", desc: "Servicio, cliente, presupuesto. En 30 segundos.", icon: ScanLine },
            { n: 2, color: "#F4B4C4", title: "Yo construyo la oferta", desc: "4 packs con precios reales anclados a tu presupuesto.", icon: Layers },
            { n: 3, color: "#C9C7F7", title: "Tú la envías", desc: "Mensaje listo + respuesta a rebaja preparada.", icon: FileText },
          ].map((s) => (
            <div key={s.n} className="text-center relative" data-testid={`step-${s.n}`}>
              <div className="step-bubble mx-auto" style={{ background: s.color }}>
                {s.n}
              </div>
              <div className="mt-6 flex items-center justify-center gap-2">
                <s.icon className="w-4 h-4 text-foreground/60" strokeWidth={2} />
                <h3 className="font-display text-xl font-black tracking-tight">{s.title}</h3>
              </div>
              <p className="mt-2 text-sm text-foreground/65 max-w-[24ch] mx-auto leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          SECCIÓN 4 — Mockup Notion-style con sticky-notes anotadas
          ────────────────────────────────────────────────────────────── */}
      <section id="what" className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-[1fr_1.3fr] gap-12 items-center">
          <div>
            <span className="pill mb-4"><span className="w-1.5 h-1.5 rounded-full bg-[#EAC119]" /> Así de fácil</span>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-[-0.03em] leading-[1.02]">
              Un sistema que entiende el negocio creativo.
            </h2>
            <p className="mt-5 text-foreground/65 leading-relaxed">
              Sin rodeos, tecnicismos ni precios inflados. Te ayuda a estructurar servicios realistas, establecer límites claros de entrega y generar un mensaje impecable, listo para enviar a tu cliente.
            </p>
            <p className="mt-4 text-foreground/65 leading-relaxed">
              Creada por Alba, diseñadora, empresaria y fundadora de <span className="font-semibold text-foreground">ABSTUDIO</span>.
            </p>
            <ul className="mt-7 space-y-3 text-sm">
              {[
                "4 opciones con precios anclados a tu presupuesto",
                "Límites de entrega claros — siempre",
                "Respuesta lista cuando te pidan rebaja",
                "Análisis de cliente con score 0-100",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Mockup */}
          <div className="relative">
            <div className="blob blob-peach w-[280px] h-[280px] -top-8 -right-8" />
            <div className="blob blob-mint w-[260px] h-[260px] -bottom-10 -left-10" style={{ animationDelay: "3s" }} />
            <div className="browser-frame relative" data-testid="mockup">
              <div className="browser-bar">
                <span className="dot bg-[#FF5F57]" />
                <span className="dot bg-[#FEBC2E]" />
                <span className="dot bg-[#28C840]" />
                <span className="ml-3 text-[10px] font-mono text-foreground/40">strato.app/builder</span>
              </div>
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55">Pack recomendado</div>
                <div className="font-display text-2xl font-black mt-1">Estratégico · 1.200€ – 1.500€</div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-[10px]">
                  <div className="rounded-lg bg-[#FAFAF7] border border-black/5 p-2"><div className="text-foreground/50">Plazo</div><div className="font-bold mt-0.5">4 semanas</div></div>
                  <div className="rounded-lg bg-[#FAFAF7] border border-black/5 p-2"><div className="text-foreground/50">Revisiones</div><div className="font-bold mt-0.5">2 rondas</div></div>
                  <div className="rounded-lg bg-[#FAFAF7] border border-black/5 p-2"><div className="text-foreground/50">Soporte</div><div className="font-bold mt-0.5">30 días</div></div>
                </div>
                <div className="mt-4 space-y-1.5 text-xs">
                  <div className="flex gap-2"><Check className="w-3 h-3 text-emerald-600 mt-0.5" /> Identidad: logo + 4 colores + 2 tipografías</div>
                  <div className="flex gap-2"><Check className="w-3 h-3 text-emerald-600 mt-0.5" /> 9 posts redes sociales (plantillas Canva)</div>
                  <div className="flex gap-2"><Check className="w-3 h-3 text-emerald-600 mt-0.5" /> Aplicación en 2 mockups (etiqueta + bolsa)</div>
                  <div className="flex gap-2"><Check className="w-3 h-3 text-emerald-600 mt-0.5" /> Mini guía de uso (PDF 3 páginas)</div>
                </div>
                <div className="mt-4 rounded-xl bg-[#1D1D1B] text-white p-3">
                  <div className="text-[9px] uppercase tracking-wider text-white/55">Mensaje listo para enviar</div>
                  <div className="text-[11px] mt-1 leading-relaxed">"Te paso la propuesta estratégica que prepramos. Si encaja, podemos arrancar la semana que viene…"</div>
                </div>
              </div>
            </div>
            {/* Sticky note anotación */}
            <div className="sticky-note absolute -bottom-6 -left-6 max-w-[180px] text-[12px] hidden md:block z-10">
              <strong>Anclado a tu presupuesto.</strong><br/>Nunca infla los precios.
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          SECCIÓN 5 — Antes / Después (comparativa visual)
          ────────────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-[-0.03em]">
            Antes vs. con Strato
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <div className="rounded-3xl border border-black/[0.08] bg-white p-7" data-testid="before-card">
            <div className="text-[11px] uppercase tracking-wider font-bold text-foreground/50 mb-4">Antes</div>
            <ul className="space-y-3 text-sm">
              {[
                "Cotizar a ojo o copiar plantillas viejas",
                "Sentir que regalas trabajo en revisiones",
                "No saber cómo responder a la rebaja",
                "Inflar entregables en vez de valor",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2 text-foreground/65">
                  <span className="w-4 h-4 rounded-full bg-[#F4B4C4] mt-0.5 shrink-0 grid place-items-center text-[10px]">×</span>
                  <span className="line-through decoration-foreground/20">{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-[#0A0A0A] text-white p-7 relative overflow-hidden" data-testid="after-card">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#A8D5BA]/20 blur-2xl" />
            <div className="text-[11px] uppercase tracking-wider font-bold text-white/55 mb-4 relative">Con Strato</div>
            <ul className="space-y-3 text-sm relative">
              {[
                "4 packs con precios reales y justificados",
                "Máximo 2 rondas, siempre. Sin discusión.",
                "Respuesta a rebaja lista para copiar/pegar",
                "Valor estratégico, no más entregables",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A8D5BA] mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          SECCIÓN 6 — Infografía: 3 números grandes con blobs detrás
          ────────────────────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden">
        <div className="blob blob-lemon w-[520px] h-[520px] top-0 left-1/3" />
        <div className="blob blob-coral w-[380px] h-[380px] bottom-0 right-1/4" style={{ animationDelay: "3s" }} />
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="grid md:grid-cols-3 gap-10 text-center">
            {[
              { v: "+45%", l: "subida media de precio que cobran nuestros usuarios" },
              { v: "2 min", l: "lo que tardas en tener tu primera oferta lista" },
              { v: "<$6", l: "al mes. menos que un café especial" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-6xl md:text-7xl font-black tracking-[-0.04em]">{s.v}</div>
                <p className="mt-3 text-sm text-foreground/65 max-w-[22ch] mx-auto">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          SECCIÓN 7 — Pricing minimalista 2 cards
          ────────────────────────────────────────────────────────────── */}
      <section id="pricing" className="max-w-5xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <span className="pill mb-4"><span className="w-1.5 h-1.5 rounded-full bg-[#A8D5BA]" /> Precio simple</span>
          <h2 className="mt-4 font-display text-4xl md:text-6xl font-black tracking-[-0.03em] leading-[1]">
            Elige tu plan.<br />
            <span className="text-foreground/45">5 días gratis, sin compromiso.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <div className="rounded-3xl border-2 border-black/[0.08] bg-white p-8 hover:border-foreground transition-colors" data-testid="pricing-monthly">
            <div className="text-[11px] uppercase tracking-wider font-bold text-foreground/55">Mensual</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-display text-6xl font-black">$6</span>
              <span className="text-foreground/55 text-sm">/mes</span>
            </div>
            <p className="mt-3 text-sm text-foreground/65">Flexible. Cancela cuando quieras.</p>
            <button onClick={go} className="mt-6 btn-pill w-full justify-center bg-foreground text-white hover:bg-black text-sm">
              Empezar prueba
            </button>
          </div>
          <div className="rounded-3xl bg-foreground text-white p-8 relative" data-testid="pricing-yearly">
            <div className="absolute -top-3 left-8 bg-[#EAC119] text-foreground text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider">
              Ahorras 32%
            </div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-white/55">Anual</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-display text-6xl font-black">$49</span>
              <span className="text-white/55 text-sm">/año</span>
            </div>
            <p className="mt-3 text-sm text-white/70">Equivale a $4.08/mes. Te ahorras $23.</p>
            <button onClick={go} className="mt-6 btn-pill w-full justify-center bg-[#EAC119] text-foreground hover:bg-yellow-300 text-sm font-bold">
              Empezar prueba
            </button>
          </div>
        </div>
        <p className="text-center mt-8 text-xs text-foreground/50">Sin permanencia. Cancela en 1 click. Pago seguro con Stripe.</p>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          SECCIÓN 8 — CTA final tipo Apple con blob
          ────────────────────────────────────────────────────────────── */}
      <section className="relative py-32 overflow-hidden">
        <div className="blob blob-mint w-[600px] h-[600px] top-0 left-1/2 -translate-x-1/2" />
        <div className="blob blob-rose w-[400px] h-[400px] bottom-0 right-10" style={{ animationDelay: "2s" }} />
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 className="font-display text-5xl md:text-7xl font-black tracking-[-0.04em] leading-[0.98]">
            Tu próximo cliente<br />
            merece una{" "}
            <span className="scribble-underline scribble-lavender">propuesta mejor.</span>
          </h2>
          <p className="mt-7 text-foreground/65 max-w-lg mx-auto">
            En 2 minutos sabes si vale la pena aceptarlo, qué cobrarle, y tienes el mensaje listo.
          </p>
          <button onClick={go} className="mt-10 btn-pill bg-foreground text-white hover:bg-black text-base px-8 py-4" data-testid="cta-final">
            Empezar mis 5 días gratis <ArrowRight className="w-4 h-4" />
          </button>
          <p className="mt-3 text-xs text-foreground/50">No se cobra hasta el día 6 · cancela cuando quieras</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-black/[0.06] bg-foreground/[0.015]">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-foreground grid place-items-center">
                <span className="deco-star text-white w-3.5 h-3.5" />
              </div>
              <span className="font-display leading-none">
                <span className="font-black">strato</span>
                <span className="font-normal text-foreground/55 ml-1.5 text-sm">by abstudio</span>
              </span>
            </div>
            <Link
              to="/legal"
              data-testid="footer-legal-cta"
              className="btn-apple-outline text-xs"
            >
              <Scale className="w-3.5 h-3.5" /> Información legal completa
            </Link>
          </div>

          <div className="border-t border-black/[0.06] pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground/65">
              <Link to="/privacidad" className="hover:text-foreground transition" data-testid="footer-privacidad">Privacidad</Link>
              <Link to="/terminos" className="hover:text-foreground transition" data-testid="footer-terminos">Términos</Link>
              <Link to="/cookies" className="hover:text-foreground transition" data-testid="footer-cookies">Cookies</Link>
              <Link to="/aviso-legal" className="hover:text-foreground transition" data-testid="footer-aviso">Aviso legal</Link>
              <a href="mailto:albabaenastudio@gmail.com" className="hover:text-foreground transition">Contacto</a>
            </nav>
            <p className="text-xs text-foreground/55">© {new Date().getFullYear()} ALBA BAENA RUIZ LTDA. · Hecho con cuidado para creativos</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
